/* eslint-disable camelcase */

import { NETWORK_ERROR_PATTERNS } from "common/utils/app-monitoring/log-upload-error";

const CLIENT_NOISE_PATTERNS = NETWORK_ERROR_PATTERNS.filter(
  pattern => pattern.toString() !== "/timeout/i"
);

const NOISE_ERROR_PATTERNS = [
  ...CLIENT_NOISE_PATTERNS,
  /Client Closed Request/i, // CloudFront client disconnect (status 499)
  /CanceledError/i // Axios request cancellation
];

// CloudFront WAF blocks — not actionable from app code
const CLOUDFRONT_BLOCK_PATTERN = /cloudfront/i;
// Bots hitting disallowed paths — expected noise
const ROBOTS_BLOCK_PATTERN = /robots disallow|robots\.txt/i;
// Known search and social crawlers. Bot traffic is not real-user RUM; any error
// a crawler hits is either expected noise (WAF blocks, robots.txt denials) or
// surfaced to us through server-side observability (Rails logs, AppMonitoring).
// Note: "whatsapp" is intentionally omitted — WhatsApp's in-app browser (real
// users tapping shared links) appends "WhatsApp/x.y.z" to a normal Chrome UA,
// and its link-preview crawler is already matched by "facebookexternalhit".
export const CRAWLER_UA_PATTERN =
  /bingbot|googlebot|applebot|duckduckbot|yandexbot|baiduspider|sogou|petalbot|ahrefsbot|semrushbot|mj12bot|dotbot|meta-externalads|facebookexternalhit|linkedinbot|twitterbot|slackbot|telegrambot|discordbot|pinterestbot/i;
// Infrastructure noise — not actionable from app code, tracked server-side
// 429 = rate-limited, 499 = client closed request
// 502/503/504 = gateway errors from Envoy/Istio/nginx during deploys and pod scaling
const IGNORED_STATUSES = new Set([429, 499, 502, 503, 504]);

export const isCrawlerUserAgent = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return typeof ua === "string" && CRAWLER_UA_PATTERN.test(ua);
};

const isNonEmptyString = value => typeof value === "string" && value.length > 0;

const safeJsonStringify = value => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toResponseBodyString = error => {
  const response = error?.response;

  if (typeof response === "string") return response;

  if (response?.data) {
    return typeof response.data === "string" ? response.data : safeJsonStringify(response.data);
  }

  if (
    response &&
    typeof response === "object" &&
    !("status" in response) &&
    !("data" in response)
  ) {
    return safeJsonStringify(response);
  }

  return "";
};

const toSearchableString = error =>
  [error?.message, error?.error, toResponseBodyString(error), error?.stack]
    .filter(isNonEmptyString)
    .join(" | ");

/**
 * Returns true if the error is worth sending to browser RUM via the notify shim.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export default function isRelevantBrowserError(error) {
  // Drop anything originating from a known crawler. Search and social bots
  // aren't real-user RUM; their errors show up as WAF blocks, robots.txt
  // denials, and pre-render quirks that server-side logs already capture.
  if (isCrawlerUserAgent()) return false;

  const status = error?.status ?? error?.response?.status;
  const haystack = toSearchableString(error);

  // Drop rate-limited and client-closed-request responses
  if (IGNORED_STATUSES.has(status)) return false;
  // Drop CloudFront WAF 403s (status-gated — CloudFront can appear in legitimate 5xx bodies)
  if (status === 403 && CLOUDFRONT_BLOCK_PATTERN.test(haystack)) return false;
  // Drop empty-body 403s — real app authorization failures include a JSON
  // payload; an empty body means the response came from edge infrastructure
  // (WAF / CDN bot challenge) and is not actionable from app code.
  if (status === 403 && toResponseBodyString(error) === "") return false;

  // Report application server errors (500, 501) — 502/503/504 are filtered above by IGNORED_STATUSES
  if (typeof status === "number" && status >= 500) return true;

  // Drop robots.txt / robots-disallow noise from bots — placed after the 5xx guard
  // so a 500 whose body mentions robots.txt is still reported as an application error.
  // Gated on "no HTTP status or 403" so a real 4xx application error whose body
  // happens to mention robots.txt is still reported; HB-1743 needs 403+robots filtered.
  if ((typeof status !== "number" || status === 403) && ROBOTS_BLOCK_PATTERN.test(haystack))
    return false;

  // Drop network/cancellation noise that is not actionable from app code.
  // Only apply to errors without an HTTP status — a real 4xx whose response body
  // happens to contain "canceled" or "offline" must still be reported.
  if (typeof status !== "number" && NOISE_ERROR_PATTERNS.some(pattern => pattern.test(haystack)))
    return false;

  return true;
}

/* eslint-enable camelcase */
